import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Swal from "sweetalert2";
import {
  sendMessage  as socketSendMessage,
  getHistory   as socketGetHistory,
  markRead     as socketMarkRead,
  onReceiveMessage,
  onMessageSent,
  onChatHistory,
  onMessagesRead,
} from "../services/socket";

// ─── License helpers ──────────────────────────────────────────────────────────
const isCallChatAllowed = () => {
  try {
    return JSON.parse(localStorage.getItem('license_call_chat') || '{"allowed":true}').allowed !== false;
  } catch { return true; }
};

const showCallChatBlocked = () => {
  const reason = (() => {
    try { return JSON.parse(localStorage.getItem('license_call_chat') || '{}').reason || ''; } catch { return ''; }
  })();
  Swal.fire({
    icon: 'error',
    title: '🔒 Call & Chat License Expired',
    html: `<p style="color:#64748b;font-size:14px">${reason || 'Your Call & Chat license has expired. Contact TotalCare support.'}</p>`,
    confirmButtonText: 'OK',
    confirmButtonColor: '#d33',
  });
};
// ─────────────────────────────────────────────────────────────────────────────

const ChatContext = createContext(null);

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used inside <ChatProvider>");
  return ctx;
};

export const ChatProvider = ({ user, children }) => {
  const [conversations, setConversations] = useState({});
  const [activeChat, setActiveChat]       = useState(null);
  const [isChatOpen, setIsChatOpen]       = useState(false);

  const activeChatRef = useRef(null);
  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  const userIdStr = String(user?.trans_code || "");

  useEffect(() => {
    if (!userIdStr) return;

    const offReceive = onReceiveMessage((msg) => {
      const otherId = String(msg.senderId);

      setConversations((prev) => {
        const conv   = prev[otherId] || { messages: [], unread: 0, user: null };
        const isOpen = activeChatRef.current === otherId;
        return {
          ...prev,
          [otherId]: {
            ...conv,
            messages: [...conv.messages, msg],
            unread:   isOpen ? 0 : conv.unread + 1,
          },
        };
      });

      if (activeChatRef.current === otherId) {
        socketMarkRead(userIdStr, otherId);
      }
    });

    const offSent = onMessageSent((msg) => {
      const otherId = String(msg.receiverId);
      setConversations((prev) => {
        const conv = prev[otherId] || { messages: [], unread: 0, user: null };
        if (conv.messages.some((m) => m.id === msg.id)) return prev;
        return {
          ...prev,
          [otherId]: {
            ...conv,
            messages: conv.messages.map((m) =>
              m._temp && m.text === msg.text ? msg : m
            ),
          },
        };
      });
    });

    const offHistory = onChatHistory(({ withUserId, messages: hist }) => {
      const otherId = String(withUserId);
      setConversations((prev) => ({
        ...prev,
        [otherId]: {
          ...(prev[otherId] || { unread: 0, user: null }),
          messages: hist,
          unread:   0,
        },
      }));
    });

    const offRead = onMessagesRead(({ byUserId }) => {
      const otherId = String(byUserId);
      setConversations((prev) => {
        if (!prev[otherId]) return prev;
        return {
          ...prev,
          [otherId]: {
            ...prev[otherId],
            messages: prev[otherId].messages.map((m) =>
              String(m.senderId) === userIdStr ? { ...m, read: true } : m
            ),
          },
        };
      });
    });

    return () => {
      offReceive();
      offSent();
      offHistory();
      offRead();
    };
  }, [userIdStr]);

  const openChat = useCallback((targetUser) => {
    // ── LICENSE CHECK ─────────────────────────────────────────────────────
    if (!isCallChatAllowed()) { showCallChatBlocked(); return; }
    // ─────────────────────────────────────────────────────────────────────

    const otherId = String(targetUser.id);

    setConversations((prev) => ({
      ...prev,
      [otherId]: {
        messages: [],
        unread:   0,
        ...(prev[otherId] || {}),
        user:   targetUser,
        unread: 0,
      },
    }));

    setActiveChat(otherId);
    setIsChatOpen(true);

    socketGetHistory(userIdStr, otherId);
    socketMarkRead(userIdStr, otherId);
  }, [userIdStr]);

  const sendMessage = useCallback((toId, text) => {
    // ── LICENSE CHECK ─────────────────────────────────────────────────────
    if (!isCallChatAllowed()) { showCallChatBlocked(); return; }
    // ─────────────────────────────────────────────────────────────────────

    if (!text?.trim()) return;

    const tempMsg = {
      id:         `_temp_${Date.now()}`,
      senderId:   userIdStr,
      receiverId: String(toId),
      text:       text.trim(),
      timestamp:  new Date().toISOString(),
      read:       false,
      _temp:      true,
    };

    setConversations((prev) => {
      const conv = prev[String(toId)] || { messages: [], unread: 0, user: null };
      return {
        ...prev,
        [String(toId)]: {
          ...conv,
          messages: [...conv.messages, tempMsg],
        },
      };
    });

    socketSendMessage(userIdStr, String(toId), text);
  }, [userIdStr]);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    setActiveChat(null);
  }, []);

  const totalUnread = useMemo(
    () => Object.values(conversations).reduce((sum, c) => sum + (c.unread || 0), 0),
    [conversations]
  );

  const value = useMemo(() => ({
    conversations,
    activeChat,
    isChatOpen,
    totalUnread,
    currentUserId: userIdStr,
    openChat,
    sendMessage,
    closeChat,
    setActiveChat,
  }), [
    conversations,
    activeChat,
    isChatOpen,
    totalUnread,
    userIdStr,
    openChat,
    sendMessage,
    closeChat,
  ]);

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};